import React from 'react'
import CountUp from 'react-countup'
import { FormattedMessage } from 'react-intl'
import { getSplitNumeralFormat } from '../../../../../../helpers/utils'
import { Text } from '../../../../..'
import { useUniqueId } from '../../../../../../hooks'

import styles from './PriceCounter.module.scss'

// Order of numeral suffixes
// k: thousand
// m: million
// b: billion
// t: trillion (does not exceed t at this time)
const SUFFIX_ORDER = 'kmbt'

const sharedCountupProps = hasPriorCalc => ({
  separator: ',',
  prefix: '$',
  className: styles.countUp,
  duration: hasPriorCalc ? 0.25 : 0,
})

const getMaxPriceCounter = ({ affordability: a, lastAffordability: l }) => {
  const [affordability, affordabilitySuffix] = getSplitNumeralFormat(a)
  const [lastAffordability, lastAffordabilitySuffix] = getSplitNumeralFormat(l)

  return {
    affordability, // Number 1.23
    affordabilitySuffix, // String m
    lastAffordability, // Number 123
    lastAffordabilitySuffix, // String k
  }
}

const MaxPriceCounter = affordabilityData => {
  let {
    affordability,
    affordabilitySuffix,
    lastAffordability,
    lastAffordabilitySuffix,
  } = getMaxPriceCounter(affordabilityData)
  const decimals = String(affordability).split('.')[1] || ''

  /*
    This sections smooths out numeral transitions when crossing from
    thousands to millions, etc
    The problem is that the animation will scroll down then going from
    500k to 1.00m (500.00 scrolling down to 1.00, as decimals are calculated
    based on affordability)
    This offsets it so that when affordability increases by a suffix
    it renders a transition from 0.50m to 1.00m. This problem is
    not noticeable when decreasing suffixes
   */
  const affordabilityIndex = SUFFIX_ORDER.indexOf(affordabilitySuffix)
  const lastAffordabilityIndex = SUFFIX_ORDER.indexOf(lastAffordabilitySuffix)

  if (
    lastAffordabilityIndex &&
    lastAffordabilityIndex !== -1 &&
    affordabilityIndex > lastAffordabilityIndex
  ) {
    lastAffordability /= 1000
    lastAffordabilitySuffix = SUFFIX_ORDER[lastAffordabilityIndex + 1]
  }

  const { className, ...sharedProps } = sharedCountupProps(
    affordabilityData.lastAffordability
  )

  return (
    <span className={className}>
      <CountUp
        {...sharedProps}
        decimals={decimals.length}
        start={lastAffordability || 0}
        end={affordability}
        key={affordability}
      />
      {affordabilitySuffix.toUpperCase()}
    </span>
  )
}

const PriceCounter = ({
  affordability,
  monthlyPayment,
  lastAffordability,
  lastMonthlyPayment,
}) => {
  const counterId = useUniqueId('PricePointTab_MaxPriceCounter_')
  const countupId = useUniqueId('PricePointTab_CountUp_')

  return (
    <div className={styles.countupContainer}>
      <div className={styles.countupGroup}>
        <label htmlFor={counterId}>
          <Text element='span' variant='neutralCool' variantCount='900'>
            <FormattedMessage
              id='Affordability.priceCounter.calculatedTotal'
              defaultMessage='Calculated price point:'
            />
          </Text>
        </label>
        <MaxPriceCounter
          id={counterId}
          affordability={affordability}
          monthlyPayment={monthlyPayment}
          lastAffordability={lastAffordability}
          lastMonthlyPayment={lastMonthlyPayment}
        />
      </div>
      <div className={styles.countupGroup}>
        <label htmlFor={countupId}>
          <Text variant='neutralCool' variantCount='900'>
            <FormattedMessage
              id='Affordability.priceCounter.monthlyPayment'
              defaultMessage='Monthly payment:'
            />
          </Text>
          <Text
            variant='neutralWarm'
            variantCount='500'
            className={styles.sublabel}
          >
            <FormattedMessage
              id='Affordability.priceCounter.monthlyPaymentTip'
              defaultMessage='(principal and interest)'
            />
          </Text>
        </label>
        <CountUp
          {...sharedCountupProps(lastAffordability)}
          id={countupId}
          start={lastMonthlyPayment || 0}
          end={monthlyPayment}
          key={monthlyPayment}
        />
      </div>
    </div>
  )
}

export default PriceCounter
